import { normalizeDate } from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import { type TFunction } from "i18next";
import { z } from "zod";
import { serviceSubtypeParticularities } from "../../form/utils";

const LANG_BASE = "vessel-call.port-services";

export const validateRts = (
  rts: unknown,
  ctx: z.RefinementCtx,
  t: TFunction,
  withinHours?: number,
) => {
  if (rts === undefined || rts === null || rts === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.required", {
        label: t(`${LANG_BASE}.fields.rts`),
      }),
      path: ["rts"],
    });
    return;
  }
  const normalizedRts = normalizeDate(rts as string | Date);

  if (!normalizedRts) {
    return;
  }

  const now = new Date();

  const maxAllowedRts = withinHours ? new Date(now.getTime() + withinHours * 60 * 60 * 1000) : null;

  if (normalizedRts < now) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.must-not-be-in-past", {
        label: t(`${LANG_BASE}.fields.rts`),
      }),
      path: ["rts"],
    });
  }

  if (maxAllowedRts && withinHours && normalizedRts > maxAllowedRts) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.must-be-within-hours", {
        label: t(`${LANG_BASE}.fields.rts`),
        hours: withinHours,
      }),
      path: ["rts"],
    });
  }
};

export const validateQuantity = (quantity: unknown, ctx: z.RefinementCtx, t: TFunction) => {
  if (quantity === undefined || quantity === null || quantity === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["quantity"],
      message: t("form-validation.required", {
        label: t(`${LANG_BASE}.fields.quantity`),
      }),
    });
    return;
  }

  const num = Number(quantity);

  if (num < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["quantity"],
      message: t("form-validation.mandatory-min", {
        label: t(`${LANG_BASE}.fields.quantity`),
        min: 1,
      }),
    });
  }
};

export const validateUnit = (unit: unknown, ctx: z.RefinementCtx, t: TFunction) => {
  if (unit === undefined || unit === null || unit === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["unitOfMeasurement"],
      message: t("form-validation.required", {
        label: t(`${LANG_BASE}.fields.unit-of-measurement`),
      }),
    });
  }
};

export const validateNumberOfSkips = (
  numberOfSkips: unknown,
  ctx: z.RefinementCtx,
  t: TFunction,
) => {
  if (numberOfSkips === undefined || numberOfSkips === null || numberOfSkips === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["numberOfSkips"],
      message: t("form-validation.required", {
        label: t(`${LANG_BASE}.fields.number-of-skips`),
      }),
    });
    return;
  }
  if (Number(numberOfSkips) < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["numberOfSkips"],
      message: t("form-validation.mandatory-min", {
        label: t(`${LANG_BASE}.fields.number-of-skips`),
        min: 1,
      }),
    });
  }
  if (!/^\d+$/.test(numberOfSkips as string)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["numberOfSkips"],
      message: t("form-validation.invalid-number", {
        label: t(`${LANG_BASE}.fields.number-of-skips`),
      }),
    });
  }
};

export const validateAttachmentDocuments = (
  data: unknown,
  ctx: z.RefinementCtx,
  t: (key: string, params?: Record<string, any>) => string,
) => {
  const selectedWasteSubtype = (data as any).serviceSubType || (data as any).serviceType;
  const uploadConfig = selectedWasteSubtype
    ? serviceSubtypeParticularities[selectedWasteSubtype]
    : undefined;
  const uploadTitle = uploadConfig?.uploadTitle ?? "additional-documents";
  const uploadFieldName = uploadConfig?.uploadFieldName ?? "attachmentDocuments";
  const uploadDocuments = (data as any)[uploadFieldName];

  if (uploadDocuments === undefined || uploadDocuments === null || uploadDocuments.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.required", {
        label: t(`${LANG_BASE}.fields.${uploadTitle}`),
      }),
      path: [uploadFieldName],
    });
  }
};
export const validateMarpolAnnexI = (wastes: unknown, ctx: z.RefinementCtx, t: TFunction) => {
  if (!Array.isArray(wastes) || wastes.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.required", {
        label: t("vessel-call.marpol-annex1.sidebar.fields.wasteResiduesForDischargeFacility"),
      }),
      path: ["wasteResiduesForDischargeFacility"],
    });
  }
};
